import React, { useState, useCallback, useMemo, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useHistory } from "react-router-dom";

import "../../../forms-common.scss";
import PageLayout from "../../_layout/PageLayout";
import DataGrid from "../../../components/datatable/DataGrid";
import {
  CreateProject,
  DeleteProject,
  ManageDeletedData,
  ViewProjectList,
  ViewProjectsAssignedTo,
} from "../../../services/authorization/Permissions";
import DeleteButton from "../../../components/buttons/delete-button/DeleteButton";
import AddNewButton from "../../../components/buttons/add-new-button/AddNewButton";
import {
  ActionsSection,
  ButtonsSection,
} from "../../../components/data-components/sections/DataSections";
import useErrorHandler, {
  ApplicationError,
} from "../../../services/error-handler/ErrorHandlerService";
import { permissionsService } from "../../../services/authorization/UserPermissionsService";
import { api } from "../../../services/api/ApiService";
import { ProjectsStorageModel } from "../../../services/project/ProjectsStorage";
import { snackbarNotificationsService } from "../../../services/notifications/SnackbarNotificationsService";
import { breadcrumbsStorage } from "../../../services/breadcrumbs/BreadcrumbsStorage";

export default observer(() => {
  const history = useHistory();
  const { handleErrorInSnackbar } = useErrorHandler();

  const [projectsStorage] = useState(() => ProjectsStorageModel.create({ useGridFiltering: true }, { api }));

  const isAddProjectDisabled = useMemo(
    () => !permissionsService.userHasPermissionTo(CreateProject),
    []
  );

  const isDeleteDisabled = useCallback(
    () =>
      !permissionsService.userHasPermissionTo(DeleteProject) || !projectsStorage.anyItemsSelected,
    [projectsStorage.anyItemsSelected]
  );

  const deleteSelectedProjects = useCallback(async () => {
    const selectedProjectsCount = projectsStorage.selectedItemsCount;
    try {
      await projectsStorage.deleteProjects(projectsStorage.selectedItems);
      snackbarNotificationsService.showSuccess("ProjectsPage.delete.success", {
        projectsCount: selectedProjectsCount,
      });
    } catch (error) {
      const errorContext = { projectsCount: selectedProjectsCount };
      const applicationError = error as ApplicationError;
      applicationError.withContext(errorContext);
      applicationError.withErrorMessage("default-error.projects.delete");
      handleErrorInSnackbar(applicationError);
    }
  }, [projectsStorage, handleErrorInSnackbar]);

  useEffect(() => {
    breadcrumbsStorage.updateBreadcrumbs(history.location.pathname);
  }, [history.location.pathname]);

  return (
    <PageLayout
      accessWithOneOfThePermissions={[ViewProjectList, ViewProjectsAssignedTo]}
      titleKey="general.projects"
    >
      <ActionsSection>
        <ButtonsSection>
          <AddNewButton
            disabled={isAddProjectDisabled}
            onClick={() => history.push("/projects/create-project")}
          />
          <DeleteButton
            disabled={isDeleteDisabled()}
            confirmationAction={deleteSelectedProjects}
            confirmationModalTitleKey="ProjectsPage.delete.confirmation.title"
            confirmationModalTitleContext={{ projectsCount: projectsStorage.selectedItemsCount }}
            confirmationModalSubtitleKey="ProjectsPage.delete.confirmation"
            canUserRestoreDeletedEntity={permissionsService.userHasPermissionTo(ManageDeletedData)}
          />
        </ButtonsSection>
      </ActionsSection>
      <DataGrid
        itemTypeKey="general.projects"
        rowClickPath="/projects/:id"
        dataGridStorage={projectsStorage}
      />
    </PageLayout>
  );
});
